/**
 * What one collected item yielded — for ANY intel source (video, post, article): its analysis state,
 * the report (an artifact), the extracted insights, and the moments looked at. Part of the unified
 * source screen's content list; there is no per-platform view. Insight lines use the same shared
 * formatter as the conversation cards, so an insight reads the same in chat and here.
 */
import { useQuery } from '@tanstack/react-query';
import type { IntelInsightPayload } from '@confluent/shared/db/schema';
import { INSIGHT_KIND_LABELS, formatMoment, insightLine, momentUrl } from '@confluent/shared/lib/intel-insight-format';
import {
  getArtifactOptions,
  listIntelSourceItemInsightsOptions,
} from '../../../generated/api/@tanstack/react-query.gen.js';
import { RichContentRenderer } from '../../../components/RichContentRenderer';
import { Badge } from '../../../components/ui/Badge.js';
import { Skeleton } from '../../../components/ui/Skeleton.js';
import { QueryBoundary } from '../../../components/ui/QueryBoundary.js';
import type { IntelSourceData } from '../../../types/intel-source';
import { safeHref } from '../../../lib/safe-url.js';

interface MediaAnalysisState {
  status: 'completed' | 'retrying' | 'fell_back_to_excerpt';
  model?: string;
  framesUsed?: number[];
  reportArtifactId?: number;
  errorCode?: string;
}

export function readMediaAnalysis(item: IntelSourceData): MediaAnalysisState | null {
  const media = item.payload?.media_analysis;
  if (typeof media !== 'object' || media === null) return null;
  const status = (media as { status?: unknown }).status;
  return status === 'completed' || status === 'retrying' || status === 'fell_back_to_excerpt'
    ? (media as MediaAnalysisState)
    : null;
}

function isInsightPayload(payload: Record<string, unknown>): payload is IntelInsightPayload & Record<string, unknown> {
  return typeof payload.kind === 'string' && payload.kind in INSIGHT_KIND_LABELS;
}

/** Artifact content is YAML frontmatter + markdown; show only the markdown. */
function stripFrontmatter(content: string): string {
  return content.replace(/^---\n[\s\S]*?\n---\n/, '');
}

function statusBadge(item: IntelSourceData, analysis: MediaAnalysisState | null) {
  if (analysis?.status === 'completed')
    return (
      <Badge variant="success" size="sm">
        analyzed
      </Badge>
    );
  if (item.processingStatus === 'harvested') {
    return (
      <Badge variant={analysis?.status === 'retrying' ? 'warning' : 'neutral'} size="sm">
        {analysis?.status === 'retrying' ? 'analysis retrying' : 'waiting for analysis'}
      </Badge>
    );
  }
  if (analysis?.status === 'fell_back_to_excerpt')
    return (
      <Badge variant="warning" size="sm">
        text-only analysis
      </Badge>
    );
  return null;
}

export function ItemAnalysis({ sourceId, item }: { sourceId: number; item: IntelSourceData }) {
  const analysis = readMediaAnalysis(item);
  const reportId = analysis?.reportArtifactId;

  const insightsQuery = useQuery({
    ...listIntelSourceItemInsightsOptions({ path: { id: sourceId, dataId: item.id } }),
    staleTime: 60_000,
  });
  const reportQuery = useQuery({
    ...getArtifactOptions({ path: { id: String(reportId ?? '') } }),
    enabled: reportId !== undefined,
    staleTime: 5 * 60_000,
  });

  const frames = analysis?.framesUsed ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap text-[11px] text-muted">
        {statusBadge(item, analysis)}
        {analysis?.model && <span className="font-mono">{analysis.model}</span>}
        {analysis?.errorCode && <span className="text-warning">{analysis.errorCode.replace(/_/g, ' ')}</span>}
        <span className="text-faint">Research notes only, not a trading signal.</span>
      </div>

      {reportId !== undefined && (
        <section>
          <h4 className="mb-1 text-xs font-medium text-muted">Report</h4>
          <QueryBoundary
            query={reportQuery}
            label="the analysis report"
            compact
            loading={<Skeleton height={14} width="60%" />}
          >
            {(report) =>
              report.content ? (
                <div className="text-[12.5px] text-body max-h-[420px] overflow-auto">
                  <RichContentRenderer content={stripFrontmatter(report.content)} />
                </div>
              ) : (
                <p className="text-[12px] text-faint">The report is no longer available.</p>
              )
            }
          </QueryBoundary>
        </section>
      )}

      <section>
        <h4 className="mb-1 text-xs font-medium text-muted">What was extracted</h4>
        <QueryBoundary
          query={insightsQuery}
          label="what was extracted"
          compact
          loading={<Skeleton height={14} width="60%" />}
        >
          {(payload) => {
            const insights = (payload.data ?? []).filter((insight) => isInsightPayload(insight.payload));
            if (insights.length === 0) {
              return <p className="text-[12px] text-faint">Nothing extracted from this item.</p>;
            }
            return (
              <ul className="space-y-1">
                {insights.map((insight) => {
                  const line = insightLine(insight.payload as IntelInsightPayload);
                  return (
                    <li key={insight.id} className="text-[12px] text-body">
                      <span className="font-medium">{line.label}:</span> {line.value}
                    </li>
                  );
                })}
              </ul>
            );
          }}
        </QueryBoundary>
      </section>

      {frames.length > 0 && (
        <section>
          <h4 className="mb-1 text-xs font-medium text-muted">Moments looked at</h4>
          <div className="flex flex-wrap gap-2">
            {frames.map((second) => {
              const url = momentUrl(item.sourceUrl, second);
              return url ? (
                <a
                  key={second}
                  href={safeHref(url) ?? undefined}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="font-mono text-[11px] text-accent-text hover:underline"
                >
                  {formatMoment(second)}
                </a>
              ) : (
                <span key={second} className="font-mono text-[11px] text-muted">
                  {formatMoment(second)}
                </span>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
